import React, { Suspense } from 'react';
import { createRoot } from 'react-dom/client';

// CSS load order (FE plan §3.9): Ionic core + structure first, then the app
// stylesheet (Tailwind preflight → design tokens + DS base → Ionic theme →
// Tailwind utilities). Ionic's optional sheets are deliberately not loaded.
import '@ionic/react/css/core.css';
import '@ionic/react/css/structure.css';
import './styles/index.css';

import { defineCustomElements } from '@ionic/pwa-elements/loader';
import { initFirebase } from './services/firebaseService';
import { setFcmServiceWorkerRegistration } from './services/pushService';
// Side-effect import: starts i18next so the `errors` / `common` bundles are
// loading before React mounts. See src/i18n/index.ts.
import './i18n';
import App from './App';
import { AppErrorBoundary } from './components/ui/AppErrorBoundary';
import { SnagScreen } from './components/ui/SnagScreen';

try {
  defineCustomElements(window);
} catch {
  /* Ionic PWA elements registration is non-fatal */
}

try {
  initFirebase();
} catch {
  /* Firebase (FCM only) failing at boot is non-fatal — push just stays off. */
}

// FCM service worker under a narrow scope, so the PWA service worker owns `/`.
if ('serviceWorker' in navigator) {
  navigator.serviceWorker
    .register('/firebase-messaging-sw.js', { scope: '/firebase-cloud-messaging-push-scope' })
    .then((reg) => setFcmServiceWorkerRegistration(reg))
    .catch(() => {
      /* push silently no-ops; the app keeps working */
    });
}

function render() {
  const container = document.getElementById('root');
  const root = createRoot(container!);
  try {
    root.render(
      <React.StrictMode>
        <AppErrorBoundary>
          {/* Covers the i18n http-backend's first bundle load; index.html's
              splash already paints the boot frame, so the fallback is empty. */}
          <Suspense fallback={null}>
            <App />
          </Suspense>
        </AppErrorBoundary>
      </React.StrictMode>,
    );
  } catch {
    root.render(<SnagScreen />);
  }
}

// Dev mock mode (FE plan D10): MSW serves a mock session so the shell runs
// with no backend. Dev server only — the branch is dead code in builds.
if (import.meta.env.DEV && import.meta.env.VITE_MOCK_API === 'true') {
  import('./mocks/browser')
    .then(({ startMockWorker }) => startMockWorker())
    .catch((err: unknown) => console.error('[mock] MSW failed to start', err))
    .finally(render);
} else {
  render();
}
